import { useMemo, useRef, useState, useEffect } from 'react'

import { Transaction } from 'components/ui/transaction-history/components/transaction'
import { FilterList } from 'components/ui/filter-modal-list'
import Search from 'components/ui/search'
import DatePicker from 'components/ui/transaction-history/components/date-picker'
import { DownloadCSVButton } from 'components/ui/download-csv'
import { NetworkLabel } from 'components/ui/network-label'
import { Pagination } from 'components/ui/pagination'
import NoItemFoot from 'components/ui/no-item-foot'

import FilterButton from 'pages/account/components/deposit-card/header/filter-button'
import { AddFilterPopup } from 'components/ui/transaction-history/components/filter-popup'
import DepositCard from 'pages/account/components/deposit-card'

import CoinDefaultLogo from 'images/coin-default-logo.svg'

import { formatCurrency } from 'utils/formatters'

import useOnClickOutside from 'hooks/use-on-click-outside'

import { ICurrency } from 'store/fetchers/currencies'

import { saveAs } from 'file-saver'

import cn from 'classnames'

import { addFilterData, FilterDataType, IFilterData, removeFilterData } from '../../../utils/filter-data'

import FilterTooltip from '../account/filter-tooltip'
import calcDifferenceInDays from '../../../utils/calc-difference-in-days'

import { floorNumber } from '../../../utils/floor-number'

import Skeleton from '../skeleton'

import { TransactionMain } from './components/transaction'
import { UTCFormatDate } from './components/transaction/utils'

import styles from './styles.module.scss'
import { TransactionStatusView, TransactionTypeView } from './components/transaction/enum'
import { useTranslation } from 'utils/use-translation'

export const headerCSVRows = ['Date', 'Type', 'Amount', 'Currency', 'Network', 'TxID', 'Status'].join(',') + '\r\n'

const itemsOnPage = 10

interface TransactionsHistoryProps {
  ownHeader?: boolean
  isHideTypeFilter?: boolean
  isHideAmountSorting?: boolean
  isLoading?: boolean
  saveCSVProp?: () => void
  list: TransactionMain[]
  currency?: ICurrency
  currencyLoan?: ICurrency
  fileNameCSV: string
  isDashboard?: boolean
  isLoan?: boolean
  search: string
  setSearch: (value: string) => void
  onSearchClick: () => void
}

export function TransactionsHistory({
  ownHeader,
  isHideTypeFilter,
  isHideAmountSorting,
  isLoading,
  saveCSVProp,
  list,
  currency,
  currencyLoan,
  fileNameCSV,
  isDashboard,
  isLoan,
  search,
  setSearch,
  onSearchClick,
}: TransactionsHistoryProps) {
  const { t } = useTranslation()

  const [filterData, setFilterData] = useState<IFilterData[]>([])

  const statuses = list.reduce((acc, cur) => {
    if (!acc.includes(cur.status)) {
      acc.push(cur.status)
    }
    return acc
  }, [])

  const types = isHideTypeFilter
    ? []
    : list.reduce((acc, cur) => {
        if (!acc.includes(cur.type)) {
          acc.push(cur.type)
        }
        return acc
      }, [])

  const saveCSV =
    saveCSVProp ||
    (() => {
      const rows = []

      if (startDate && endDate) {
        listForCSV
          .filter(
            (item) =>
              new Date(item.date).getTime() >= new Date(startDate).getTime() &&
              new Date(item.date).getTime() <= new Date(endDate).getTime(),
          )
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .forEach((data) => {
            const { date, type, amount, code, network, hash, status, id } = data
            const roundedAmount = floorNumber(Number(amount), 6)
            const res = [UTCFormatDate(date), type, roundedAmount, code, network, hash ?? id, status].join(',') + '\r\n'

            rows.push(res)
          })
      } else {
        listForCSV
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .forEach((data) => {
            const { date, type, amount, code, network, hash, status, id } = data
            const roundedAmount = floorNumber(Number(amount), 6)
            const res = [UTCFormatDate(date), type, roundedAmount, code, network, hash ?? id, status].join(',') + '\r\n'

            rows.push(res)
          })
      }

      saveAs(new Blob([headerCSVRows, ...rows], { type: 'text/csv;charset=utf-8' }), fileNameCSV)
    })

  const [filterPopupIsVisible, setFilterPopupIsVisible] = useState(false)

  const [showCalendar, setShowCalendar] = useState(false)
  const [startDate, setStartDate] = useState(null)

  const [endDate, setEndDate] = useState(null)

  const [dateSorting, setDateSorting] = useState<number>(2)
  const [amountSorting, setAmountSorting] = useState<number>(0)

  const sortings = [
    { label: t('date_oldest_first'), state: dateSorting, setState: setDateSorting, value: 1 },
    { label: t('date_newest_first'), state: dateSorting, setState: setDateSorting, value: 2 },
    ...(isHideAmountSorting
      ? []
      : [
          { label: t('amount_lowest_first'), state: amountSorting, setState: setAmountSorting, value: 1 },
          { label: t('amount_highest_first'), state: amountSorting, setState: setAmountSorting, value: 2 },
        ]),
  ]

  const [activePage, setActivePage] = useState(1)
  const [pagesCount, setPagesCount] = useState(Math.ceil(list.length / itemsOnPage))

  const searchTextOnChange = (searchValue: string) => {
    setSearch(searchValue)
  }

  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  const containerRef = useRef(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)
  const [showLeftGradient, setShowLeftGradient] = useState<boolean>(false)
  const [showRightGradient, setShowRightGradient] = useState<boolean>(false)

  const startDrag = (e) => {
    isDragging.current = true
    startX.current = e.pageX - containerRef.current.offsetLeft
    scrollLeft.current = containerRef.current.scrollLeft
  }

  const stopDrag = () => {
    isDragging.current = false
  }

  const handleMouseMove = (e) => {
    if (!isDragging.current) return
    e.preventDefault()
    const x = e.pageX - containerRef.current.offsetLeft
    const walk = x - startX.current // Скорость прокрутки (можно настроить)
    containerRef.current.scrollLeft = scrollLeft.current - walk
  }

  const updateGradients = () => {
    const container = containerRef.current
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setShowLeftGradient(scrollLeft > 0)
      setShowRightGradient(scrollLeft + clientWidth < scrollWidth)
    }
  }

  useEffect(() => {
    if (isDashboard && typeof window !== 'undefined' && window.localStorage) {
      const savedFilterData: IFilterData[] = JSON.parse(localStorage?.getItem('transactionHistory:filterData')) ?? []

      setDateSorting(
        Number(localStorage?.getItem('transactionHistory:dateSorting')) !== 0
          ? Number(localStorage?.getItem('transactionHistory:dateSorting'))
          : 2,
      )
      setTypeFilter(JSON.parse(localStorage?.getItem('transactionHistory:type')) ?? [])
      setStatusFilter(JSON.parse(localStorage?.getItem('transactionHistory:status')) ?? [])
      setAmountSorting(Number(localStorage?.getItem('transactionHistory:amountSorting')) ?? 0)
      setStartDate(
        localStorage?.getItem('transactionHistory:startDate') &&
          localStorage?.getItem('transactionHistory:startDate') !== 'null'
          ? new Date(localStorage?.getItem('transactionHistory:startDate'))
          : null,
      )
      setEndDate(
        localStorage?.getItem('transactionHistory:endDate') &&
          localStorage?.getItem('transactionHistory:endDate') !== 'null'
          ? new Date(localStorage?.getItem('transactionHistory:endDate'))
          : null,
      )
      // Предположим, что `savedFilterData` не содержит функцию onClick
      const updatedFilterData = savedFilterData.map((item) => {
        if (item.type === null) {
          return {
            ...item,
            onClick: () => {
              setFilterData([])
              setStatusFilter([])
              setTypeFilter([])
              setAmountSorting(0)
              setDateSorting(2)
            },
          }
        }

        if (item.type === FilterDataType.TYPE) {
          return {
            ...item,
            onClick: () => {
              setFilterData((prevFilterData) => {
                let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.TYPE)

                if (updatedFilterData.length === 2) {
                  updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
                }

                return updatedFilterData
              })
              setTypeFilter([])
            },
          }
        }

        if (item.type === FilterDataType.STATUS) {
          return {
            ...item,
            onClick: () => {
              setFilterData((prevFilterData) => {
                let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.STATUS)

                if (updatedFilterData.length === 2) {
                  updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
                }

                return updatedFilterData
              })
              setStatusFilter([])
            },
          }
        }

        if (item.type === FilterDataType.DATE) {
          return {
            ...item,
            onClick: null,
          }
        }

        if (item.type === FilterDataType.AMOUNT) {
          return {
            ...item,
            onClick: () => {
              setFilterData((prevFilterData) => {
                let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.AMOUNT)

                if (updatedFilterData.length === 2) {
                  updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
                }

                return updatedFilterData
              })
              setAmountSorting(0)
            },
          }
        }
        return item
      })
      setFilterData(updatedFilterData)
    }

    const container = containerRef.current

    if (container) {
      container.addEventListener('scroll', updateGradients)
      window.addEventListener('resize', updateGradients)

      return () => {
        container.removeEventListener('scroll', updateGradients)
        window.removeEventListener('resize', updateGradients)
      }
    }
    return undefined
  }, [])

  useEffect(() => {
    if (isDashboard) {
      localStorage.setItem('transactionHistory:filterData', JSON.stringify(filterData))
    }
    const container = containerRef.current

    if (container) {
      updateGradients()
    }
  }, [filterData])

  useEffect(() => {
    if (isDashboard) {
      localStorage.setItem('transactionHistory:type', JSON.stringify(typeFilter))
    }
  }, [typeFilter])

  useEffect(() => {
    if (isDashboard) {
      localStorage.setItem('transactionHistory:status', JSON.stringify(statusFilter))
    }
  }, [statusFilter])

  useEffect(() => {
    if (isDashboard) {
      localStorage.setItem('transactionHistory:amountSorting', amountSorting.toString())
    }
  }, [amountSorting])

  useEffect(() => {
    if (isDashboard) {
      localStorage.setItem('transactionHistory:dateSorting', dateSorting.toString())
    }
  }, [dateSorting])

  const handleTypeSelect = (types: string | string[]) => {
    if (Array.isArray(types)) {
      const setTypes = [...new Set(types)]

      setTypes.forEach((type) => {
        const index = typeFilter.indexOf(type)
        if (index !== -1) {
          const data: IFilterData = {
            type: FilterDataType.TYPE,
            content: t(typeFilter.find((item) => item !== type)?.toLowerCase()),
            data: typeFilter.find((item) => item !== type),
            count: 1,
            filters: [type],
            onClick: null,
            onDestroy: null,
          }
          const removedData = removeFilterData(filterData, data, t)
          if (!removedData.find((item) => item.type === FilterDataType.TYPE)) {
            filterData.find((item) => item.type === FilterDataType.TYPE && item.onDestroy)?.onDestroy()
          } else {
            setFilterData(removedData)
          }
        } else {
          const data: IFilterData = {
            type: FilterDataType.TYPE,
            content: type === 'Monthly interest' ? t('loan_interest') : t(type.toLowerCase()),
            count: 1,
            data: type === 'Monthly interest' ? 'Loan Interest' : type,
            filters: [type === 'Monthly interest' ? 'Loan Interest' : type],
            onClick: () => {
              setFilterData((prevFilterData) => {
                let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.TYPE)

                if (updatedFilterData.length === 2) {
                  updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
                }

                return updatedFilterData
              })
              setTypeFilter([])
            },
            onDestroy: () => {
              data.isDestroying = true
              setTimeout(() => {
                data.onClick()
                data.isDestroying = false
              }, 300)
            },
          }
          setFilterData(addFilterData(filterData, data, t))

          if (filterData.length === 2) {
            const data: IFilterData = {
              type: null,
              content: t('clear_all_filters'),
              data: 'Clear all filters',
              count: 0,
              filters: [],
              onClick: () => {
                setFilterData([])
                setTypeFilter([])
                setStatusFilter([])
              },
              onDestroy: () => {
                data.isDestroying = true
                setTimeout(() => {
                  data.onClick()
                  data.isDestroying = false
                }, 300)
              },
            }
            setFilterData(addFilterData(filterData, data, t))
          }
        }
      })
    } else {
      const type = types

      const index = typeFilter.indexOf(type)
      if (index !== -1) {
        const data: IFilterData = {
          type: FilterDataType.TYPE,
          content: t(typeFilter.find((item) => item !== type)?.toLowerCase()),
          count: 1,
          data: typeFilter.find((item) => item !== type),
          filters: [type === 'Monthly interest' ? 'Loan Interest' : type],
          onClick: null,
          onDestroy: null,
        }
        const removedData = removeFilterData(filterData, data, t)
        if (!removedData.find((item) => item.type === FilterDataType.TYPE)) {
          filterData.find((item) => item.type === FilterDataType.TYPE && item.onDestroy)?.onDestroy()
        } else {
          setFilterData(removedData)
        }
      } else {
        const data: IFilterData = {
          type: FilterDataType.TYPE,
          content: type === 'Monthly interest' ? t('loan_interest') : t(type.toLowerCase()),
          data: type === 'Monthly interest' ? 'Loan Interest' : type,
          count: 1,
          filters: [type === 'Monthly interest' ? 'Loan Interest' : type],
          onClick: () => {
            setFilterData((prevFilterData) => {
              let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.TYPE)

              if (updatedFilterData.length === 2) {
                updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
              }

              return updatedFilterData
            })
            setTypeFilter([])
          },
          onDestroy: () => {
            data.isDestroying = true
            setTimeout(() => {
              data.onClick()
              data.isDestroying = false
            }, 300)
          },
        }

        setFilterData(addFilterData(filterData, data, t))

        if (filterData.length === 2) {
          const data: IFilterData = {
            type: null,
            content: t('clear_all_filters'),
            data: 'Clear all filters',
            count: 0,
            filters: [],
            onClick: () => {
              setFilterData([])
              setStatusFilter([])
              setTypeFilter([])
            },
            onDestroy: () => {
              data.isDestroying = true
              setTimeout(() => {
                data.onClick()
                data.isDestroying = false
              }, 300)
            },
          }
          setFilterData(addFilterData(filterData, data, t))
        }
      }
    }

    setTypeFilter((prevTypeFilter) => {
      if (Array.isArray(types)) {
        const newTypes = [...prevTypeFilter]
        const setTypes = [...new Set(types)]

        setTypes.forEach((type) => {
          const index = newTypes.indexOf(type)

          if (index !== -1) {
            newTypes.splice(index, 1)
          } else {
            newTypes.push(type)
          }
        })

        return newTypes
      } else {
        const type = types

        const index = prevTypeFilter.indexOf(type)
        if (index !== -1) {
          return prevTypeFilter.filter((t) => t !== type)
        } else {
          return [...prevTypeFilter, type]
        }
      }
    })
  }

  const handleStatusSelect = (statuses: string | string[]) => {
    if (Array.isArray(statuses)) {
      statuses.forEach((s) => {
        const index = statusFilter.indexOf(s)

        if (index !== -1) {
          const data: IFilterData = {
            type: FilterDataType.STATUS,
            content: t(statusFilter.find((item) => item !== s)?.toLowerCase()),
            data: statusFilter.find((item) => item !== s),
            count: 1,
            filters: [s],
            onClick: null,
            onDestroy: null,
          }
          const removedData = removeFilterData(filterData, data, t)
          if (!removedData.find((item) => item.type === FilterDataType.STATUS)) {
            filterData.find((item) => item.type === FilterDataType.STATUS && item.onDestroy)?.onDestroy()
          } else {
            setFilterData(removedData)
          }
        } else {
          const data: IFilterData = {
            type: FilterDataType.STATUS,
            content: t(s.toLowerCase()),
            data: s,
            count: 1,
            filters: [s],
            onClick: () => {
              setFilterData((prevFilterData) => {
                let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.STATUS)

                if (updatedFilterData.length === 2) {
                  updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
                }

                return updatedFilterData
              })
              setStatusFilter([])
            },
            onDestroy: () => {
              data.isDestroying = true
              setTimeout(() => {
                data.onClick()
                data.isDestroying = false
              }, 300)
            },
          }

          setFilterData(addFilterData(filterData, data, t))

          if (filterData.length === 2) {
            const data: IFilterData = {
              type: null,
              content: t('clear_all_filters'),
              data: 'Clear all filters',
              count: 0,
              filters: [],
              onClick: () => {
                setFilterData([])
                setTypeFilter([])
                setStatusFilter([])
              },
              onDestroy: () => {
                data.isDestroying = true
                setTimeout(() => {
                  data.onClick()
                  data.isDestroying = false
                }, 300)
              },
            }
            setFilterData(addFilterData(filterData, data, t))
          }
        }
      })
    } else {
      const singleStatus = statuses
      const index = statusFilter.indexOf(singleStatus)
      if (index !== -1) {
        const data: IFilterData = {
          type: FilterDataType.STATUS,
          content: t(statusFilter.find((item) => item !== singleStatus)?.toLowerCase()),
          data: statusFilter.find((item) => item !== singleStatus),
          count: 1,
          filters: [statuses],
          onClick: null,
          onDestroy: null,
        }
        const removedData = removeFilterData(filterData, data, t)
        if (!removedData.find((item) => item.type === FilterDataType.STATUS)) {
          filterData.find((item) => item.type === FilterDataType.STATUS && item.onDestroy)?.onDestroy()
        } else {
          setFilterData(removedData)
        }
      } else {
        const data: IFilterData = {
          type: FilterDataType.STATUS,
          content: t(singleStatus.toLowerCase()),
          data: singleStatus,
          count: 1,
          filters: [statuses],
          isDestroying: false,
          onClick: () => {
            setFilterData((prevFilterData) => {
              let updatedFilterData = prevFilterData.filter((item) => item.type !== FilterDataType.STATUS)

              if (updatedFilterData.length === 2) {
                updatedFilterData = updatedFilterData.filter((item) => item.type !== null)
              }

              return updatedFilterData
            })
            setStatusFilter([])
          },
          onDestroy: () => {
            data.isDestroying = true
            setTimeout(() => {
              data.onClick()
              data.isDestroying = false
            }, 300)
          },
        }

        setFilterData(addFilterData(filterData, data, t))
        if (filterData.length === 2) {
          const data: IFilterData = {
            type: null,
            content: t('clear_all_filters'),
            data: 'Clear all filters',
            count: 0,
            isDestroying: false,
            filters: [],
            onClick: () => {
              setFilterData([])
              setTypeFilter([])
              setStatusFilter([])
            },
            onDestroy: () => {
              data.isDestroying = true
              setTimeout(() => {
                data.onClick()
                data.isDestroying = false
              }, 300)
            },
          }
          setFilterData(addFilterData(filterData, data, t))
        }
      }
    }

    setStatusFilter((prevStatusFilter) => {
      if (Array.isArray(statuses)) {
        const newStatuses = [...prevStatusFilter]

        statuses.forEach((s) => {
          const index = newStatuses.indexOf(s)
          if (index !== -1) {
            newStatuses.splice(index, 1)
          } else {
            newStatuses.push(s)
          }
        })
        return newStatuses
      } else {
        const singleStatus = statuses
        const index = prevStatusFilter.indexOf(singleStatus)
        if (index !== -1) {
          return prevStatusFilter.filter((s) => s !== singleStatus)
        } else {
          return [...prevStatusFilter, singleStatus]
        }
      }
    })
  }
  useEffect(() => {
    setActivePage(1)
  }, [typeFilter, statusFilter, startDate, endDate, dateSorting, amountSorting])

  const filteredList = useMemo(() => {
    let result = list.filter((item) => {
      if (item.status === TransactionStatusView.PENDING) {
        return calcDifferenceInDays(new Date(item.date), new Date()) < 1
      }
      return true // Для всех остальных статусов, просто возвращаем элемент
    })
    let filterDataArr = [...filterData]

    if (startDate && endDate) {
      const start = new Date(startDate).getTime()
      const end = new Date(endDate).getTime()
      result = result.filter((item) => {
        const itemDate = new Date(item.date).getTime()
        return itemDate > start && itemDate < end
      })
    }

    if (typeFilter.length) {
      result = result.filter((item) => typeFilter.includes(item.type))
    }

    if (statusFilter.length) {
      result = result.filter((item) => statusFilter.includes(item.status))
    }

    if (dateSorting === 0) {
      filterDataArr = filterDataArr.filter((item) => item.type !== FilterDataType.DATE)

      if (filterDataArr.length === 2) {
        filterDataArr = filterDataArr.filter((item) => item.type !== null)
      }

      setFilterData(filterDataArr)
    }

    if (amountSorting === 0) {
      filterDataArr = filterDataArr.filter((item) => item.type !== FilterDataType.AMOUNT)

      if (filterDataArr.length === 2) {
        filterDataArr = filterDataArr.filter((item) => item.type !== null)
      }

      setFilterData(filterDataArr)
    }

    if (dateSorting === 1) {
      result = result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }

    if (dateSorting === 2) {
      result = result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }

    if (amountSorting === 1) {
      result = result.sort((a, b) => Number(a.amount) - Number(b.amount))
    }

    if (amountSorting === 2) {
      result = result.sort((a, b) => Number(b.amount) - Number(a.amount))
    }

    setPagesCount(Math.ceil(result.length / itemsOnPage))
    const startIndex = (activePage - 1) * itemsOnPage
    const endIndex = Math.min(startIndex + itemsOnPage, result.length)

    result = result.slice(startIndex, endIndex)

    return result
  }, [list, typeFilter, statusFilter, startDate, endDate, dateSorting, amountSorting, activePage])

  const listForCSV = useMemo(() => {
    let result = [...list]

    if (startDate && endDate) {
      const start = new Date(startDate).getTime()
      const end = new Date(endDate).getTime()
      result = result.filter((item) => {
        const itemDate = new Date(item.date).getTime()
        return itemDate > start && itemDate < end
      })
    }

    if (typeFilter.length) {
      result = result.filter((item) => typeFilter.includes(item.type))
    }

    if (statusFilter.length) {
      result = result.filter((item) => statusFilter.includes(item.status))
    }

    if (dateSorting === 1) {
      result = result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }

    if (dateSorting === 2) {
      result = result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }

    if (amountSorting === 1) {
      result = result.sort((a, b) => Number(a.amount) - Number(b.amount))
    }

    if (amountSorting === 2) {
      result = result.sort((a, b) => Number(b.amount) - Number(a.amount))
    }

    return result
  }, [list, typeFilter, statusFilter, startDate, endDate, dateSorting, amountSorting, activePage])

  const [typesModalOpened, setTypesModalOpened] = useState(false)
  const typesListRef = useRef(null)
  useOnClickOutside(typesListRef, () => setTypesModalOpened(false))

  const [statusesModalOpened, setStatusesModalOpened] = useState(false)
  const statusesListRef = useRef(null)
  useOnClickOutside(statusesListRef, () => setStatusesModalOpened(false))

  const handleChangeCalendar = (value) => {
    setStartDate(value[0])
    setEndDate(value[1])
    if (isDashboard) {
      localStorage.setItem('transactionHistory:startDate', value[0].toString())
      localStorage.setItem('transactionHistory:endDate', value[1].toString())
    }
    setShowCalendar(false)
  }

  const handleClearCalendar = () => {
    setEndDate(null)
    setStartDate(null)
    if (isDashboard) {
      localStorage.removeItem('transactionHistory:startDate')
      localStorage.removeItem('transactionHistory:endDate')
    }
  }

  const groupTxsByDate = (txs: TransactionMain[]): { [key: string]: TransactionMain[] } => {
    return txs.reduce((acc, tx) => {
      const date = new Date(tx.date)
      const formattedDate = `${date.toLocaleString('en-US', {
        month: 'short',
      })} ${date.getDate()}, ${date.getFullYear()}`
      if (!acc[formattedDate]) acc[formattedDate] = []
      acc[formattedDate].push(tx)
      return acc
    }, {})
  }

  return (
    <div className={cn(styles.txsHistory, { [styles.loan]: isLoan })}>
      {!ownHeader ? (
        <>
          {isLoan && <h2 className={cn({ [styles.loan__header_mobile]: isLoan })}>{t('transaction_history')}</h2>}
          <div className={styles.header}>
            <h2 className={cn({ [styles.loan__header]: isLoan })}>{t('transaction_history')}</h2>
            <div className={styles.coin}>
              {currency ? (
                <>
                  <img
                    alt={`${currency?.code} icon`}
                    className={styles.list__itemIcon}
                    src={currency?.logo_url}
                    onError={(evt) => {
                      evt.currentTarget.src = CoinDefaultLogo
                    }}
                  />
                  <span>{formatCurrency(currency, true)}</span>
                </>
              ) : (
                ''
              )}

              {currencyLoan ? (
                <>
                  <span className={styles.slash}>/</span>
                  <img
                    alt={`${currencyLoan.code} icon`}
                    className={styles.list__itemIcon}
                    src={currencyLoan.logo_url}
                    onError={(evt) => {
                      evt.currentTarget.src = CoinDefaultLogo
                    }}
                  />
                  <span>{formatCurrency(currencyLoan, true)}</span>
                  <NetworkLabel className={styles.networkLabel} currency={currencyLoan} truncate={true} />
                </>
              ) : (
                ''
              )}

              <DownloadCSVButton className={styles.downloadCSVOnlyMobile} onClick={saveCSV} />
            </div>
          </div>
        </>
      ) : (
        ''
      )}

      <div className={styles.searchAndFiltersOnlyMobile}>
        <Search
          className={styles.search}
          onChange={searchTextOnChange}
          value={search}
          placeholder={t('search_by_type')}
          isWithButton
          onBtnSearchClick={onSearchClick}
        />
        <FilterButton onClick={() => setFilterPopupIsVisible(true)} className={styles.filterIcon} isActive={true} />
        {filterPopupIsVisible && (
          <AddFilterPopup
            onClose={() => setFilterPopupIsVisible(false)}
            dateSelect={{
              startDate: startDate,
              endDate: endDate,
              handleChangeCalendar: handleChangeCalendar,
            }}
            typeFilter={{
              types: types,
              selectedTypes: typeFilter,
              onSelect: handleTypeSelect,
            }}
            statusFilter={{
              statuses: statuses,
              selectedStatuses: statusFilter,
              onSelect: handleStatusSelect,
            }}
            sortings={sortings}
          />
        )}
      </div>
      <div className={styles.searchAndFilters}>
        <Search
          className={styles.search}
          onChange={searchTextOnChange}
          value={search}
          placeholder={t('search_by_type')}
          isWithButton
          onBtnSearchClick={onSearchClick}
        />

        <DatePicker
          className={styles.datePicker}
          startDate={startDate}
          endDate={endDate}
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          handleChangeCalendar={handleChangeCalendar}
          handleClearCalendar={handleClearCalendar}
        />
        <DownloadCSVButton className={styles.downloadCSV} onClick={saveCSV} />
      </div>

      <div
        className={`${styles.filterTooltipsWrapper} ${showLeftGradient ? styles.showLeftGradient : ''} ${
          showRightGradient ? styles.showRightGradient : ''
        }`}
      >
        <div
          className={`${cn(styles.filterTooltips, {
            [styles.filterTooltips_empty]: new Array(...new Set(filterData)).length === 0,
          })}`}
          ref={containerRef}
          onMouseDown={startDrag}
          onMouseLeave={stopDrag}
          onMouseUp={stopDrag}
          onMouseMove={handleMouseMove}
        >
          {new Array(...new Set(filterData)).map((item, index) => (
            <FilterTooltip
              imgSrc={item.imgSrc}
              key={index}
              content={item.content}
              onClose={item.onClick}
              type={item.type}
              isDestroying={item.isDestroying}
            />
          ))}
        </div>
      </div>

      <DepositCard.ColumnsHeader className={styles.columnsHeader}>
        <DepositCard.ColumnHeaderCell
          title={t('date')}
          onClick={() => {
            setAmountSorting(0)
            setDateSorting((prev) => (prev === 1 ? 2 : 1))
          }}
          sortingState={dateSorting}
        />

        <div ref={typesListRef} className={styles.filterList}>
          {isHideTypeFilter ? (
            <DepositCard.ColumnHeaderCell title={t('type')} />
          ) : (
            <>
              <DepositCard.ColumnHeaderCell
                title={t('type')}
                onClick={() => setTypesModalOpened(!typesModalOpened)}
                isActive={typeFilter.length > 0}
                onClearSort={() => {
                  setTypeFilter([])
                  setTypesModalOpened(false)
                  filterData.find((item) => item.type === FilterDataType.TYPE && item.onClick !== null)?.onDestroy()
                }}
              />
              {typesModalOpened && (
                <FilterList filters={types} selectedFilters={typeFilter} onSelect={handleTypeSelect} />
              )}
            </>
          )}
        </div>

        {isHideAmountSorting ? (
          <DepositCard.ColumnHeaderCell title={t('amount')} />
        ) : (
          <DepositCard.ColumnHeaderCell
            title={t('amount')}
            onClick={() => {
              setDateSorting(0)
              setAmountSorting((prev) => (prev === 1 ? 2 : 1))
            }}
            sortingState={amountSorting}
          />
        )}

        <DepositCard.ColumnHeaderCell title={t('txn_hash_id')} />

        <div ref={statusesListRef} className={styles.filterList}>
          <DepositCard.ColumnHeaderCell
            title={t('status')}
            onClick={() => setStatusesModalOpened(!statusesModalOpened)}
            isActive={statusFilter.length > 0}
            onClearSort={() => {
              setStatusFilter([])
              setStatusesModalOpened(false)
              filterData.find((item) => item.type === FilterDataType.STATUS && item.onClick !== null)?.onDestroy()
              if (filterData.filter((item) => item.type !== FilterDataType.STATUS).length <= 2) {
                // filterData.find((item) => item.type === null && item.onHide)?.onHide()
              }
            }}
          />
          {statusesModalOpened && (
            <FilterList filters={statuses} selectedFilters={statusFilter} onSelect={handleStatusSelect} />
          )}
        </div>
      </DepositCard.ColumnsHeader>
      {isLoading ? (
        <div className={styles.skeletons}>
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
        </div>
      ) : (
        <div className={styles.transactions}>
          {filteredList.length > 0 &&
            [...filteredList].map((tx, index) => (
              <Transaction
                key={index}
                index={index}
                id={tx.id}
                code={tx.code}
                network={tx.network}
                incoming={tx.type !== TransactionTypeView.REPAYMENT && tx.type !== TransactionTypeView.WITHDRAWAL}
                date={tx.date}
                type={tx.type}
                amount={tx.amount}
                amountTo={tx.amountTo}
                amountFrom={tx.amountFrom}
                from_currency={tx.from_currency}
                to_currency={tx.to_currency}
                hash={tx.hash}
                status={tx.status}
              />
            ))}
          {isLoading
            ? ''
            : filteredList.length === 0 && (
                <NoItemFoot text={t('cant_find_transactions')} className={styles.noItemFoot} />
              )}
        </div>
      )}

      {isLoading ? (
        <div className={styles.skeletonsMobile}>
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
          <Skeleton className={styles.skeletons__item} />
        </div>
      ) : (
        <div className={styles.transactionsMobile}>
          {filteredList.length > 0 ? (
            Object.entries(groupTxsByDate(filteredList)).map(([date, txs]: [string, TransactionMain[]]) => (
              <div className={styles.transactiosGroupByDate} key={date}>
                <span className={styles.date}>{date}</span>
                {txs.map((tx, index) => (
                  <Transaction
                    key={index}
                    index={index}
                    id={tx.id}
                    code={tx.code}
                    network={tx.network}
                    incoming={tx.incoming}
                    date={tx.date}
                    type={tx.type}
                    amount={tx.amount}
                    amountTo={tx.amountTo}
                    amountFrom={tx.amountFrom}
                    from_currency={tx.from_currency}
                    to_currency={tx.to_currency}
                    hash={tx.hash}
                    status={tx.status}
                  />
                ))}
              </div>
            ))
          ) : isLoading ? (
            ''
          ) : (
            <NoItemFoot text={t('cant_find_transactions')} className={styles.noItemFoot} />
          )}
        </div>
      )}

      {pagesCount > 1 && (
        <Pagination pagesCount={pagesCount} activePage={activePage} onPageChange={(page) => setActivePage(page)} />
      )}
    </div>
  )
}
